/**
 * Business Partner Add/Edit Page
 *
 * Unified form for creating and editing business partners.
 * Features:
 * - Company search autocomplete (create mode)
 * - Company info + address editing
 * - Document arrangements management (receivers only)
 * - Integration with container form via returnTo flow
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router';
import { Save01, Plus, SearchLg, XCircle, Trash01 } from '@untitledui/icons';
import { BackLink } from '@/components/base/back-link/back-link';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { navigationStateFrom } from '@/types/navigation';
import { countries } from '@/data/countries';
import { toast } from '@/lib/toast-service';
import { createContact } from '@/data/api/address-book';

import { useQueryClient } from '@tanstack/react-query';
import { useMe } from '@/hooks/api/use-auth';
import { useCompany, useCreateCompany, useUpdateCompany } from '@/hooks/api/use-companies';
import { useCreateReceiverAssociation } from '@/hooks/api/use-receivers';
import { useCompanySearch } from '@/hooks/api/use-company-search';
import {
  useDocumentArrangements,
  useCreateArrangement,
  useUpdateArrangement,
  useDeleteArrangement,
} from '@/hooks/api/use-document-arrangements';
import { useUploadDocument } from '@/hooks/api/use-documents';
import {
  DocumentArrangementsSection,
  type ArrangementFormData,
} from '@/components/application/business-partners/document-arrangements-section';
import { CompanyType } from '@/types/api/admin';
import type { CompanyResponse } from '@/types/api/admin';

// ============================================================================
// HELPERS
// ============================================================================

const getCompanyTypeBadgeColor = (type: string | undefined) => {
  switch (type?.toLowerCase()) {
    case 'receiver': return 'brand';
    case 'freight_forwarder': return 'success';
    case 'carrier': return 'warning';
    case 'customs_broker': return 'error';
    case 'sender': return 'gray';
    default: return 'gray';
  }
};

const formatCompanyType = (type: string | undefined) => {
  if (!type) return '-';
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const companyTypeOptions = [
  { id: 'receiver', label: 'Receiver' },
  { id: 'freight_forwarder', label: 'Freight Forwarder' },
  { id: 'carrier', label: 'Carrier' },
  { id: 'customs_broker', label: 'Customs Broker' },
];

// ============================================================================
// FORM STATE
// ============================================================================

interface CompanyFormState {
  company_name: string;
  company_type: string;
  legal_name: string;
  tax_id: string;
  eori_number: string;
  primary_email: string;
  primary_phone: string;
  street: string;
  city: string;
  state_province: string;
  postal_code: string;
  country_code: string;
}

const initialFormState: CompanyFormState = {
  company_name: '',
  company_type: 'receiver',
  legal_name: '',
  tax_id: '',
  eori_number: '',
  primary_email: '',
  primary_phone: '',
  street: '',
  city: '',
  state_province: '',
  postal_code: '',
  country_code: '',
};

// Auto-assign role based on company type
const companyTypeToRole: Record<string, string> = {
  receiver: 'Receiver',
  carrier: 'Vettore marittimo',
  freight_forwarder: 'Freight Forwarder',
  customs_broker: 'Dogana',
};

interface KeyContact {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const BusinessPartnerFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { id: partnerId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!partnerId;

  const presetType = searchParams.get('type');
  const returnTo = searchParams.get('returnTo');

  const { data: currentUser } = useMe();
  const companyId = currentUser?.company_id;

  // Form state
  const [formState, setFormState] = useState<CompanyFormState>({
    ...initialFormState,
    company_type: presetType || 'receiver',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(!isEditMode);

  // Country search state
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: searchResults } = useCompanySearch(debouncedSearch, isSearching);

  // Filter out current user's own company from results
  const filteredSearchResults = useMemo(() => {
    if (!searchResults) return [];
    return searchResults.filter((c) => c.company_id !== companyId);
  }, [searchResults, companyId]);

  // Key contacts state
  const [keyContacts, setKeyContacts] = useState<KeyContact[]>([]);

  // Document arrangements state
  const [arrangements, setArrangements] = useState<ArrangementFormData[]>([]);

  // Edit mode: load existing company
  const { data: existingCompany, isLoading: isLoadingCompany } = useCompany(
    partnerId || '',
  );

  // Load existing arrangements for this receiver
  const { data: existingArrangements } = useDocumentArrangements(
    existingCompany?.company_name || formState.company_name || undefined,
    selectedCompanyId || partnerId || undefined,
    !!(existingCompany?.company_name || (selectedCompanyId && formState.company_name)),
  );

  // Populate form when existing company data loads
  useEffect(() => {
    if (isEditMode && existingCompany) {
      const address = existingCompany.headquarters_address as Record<string, string> | null;
      setFormState({
        company_name: existingCompany.company_name || '',
        company_type: existingCompany.company_type || 'receiver',
        legal_name: existingCompany.legal_name || '',
        tax_id: existingCompany.tax_id || '',
        eori_number: existingCompany.eori_number || '',
        primary_email: existingCompany.primary_email || '',
        primary_phone: existingCompany.primary_phone || '',
        street: address?.street || '',
        city: address?.city || '',
        state_province: address?.state_province || '',
        postal_code: address?.postal_code || '',
        country_code: address?.country_code || '',
      });
      setSelectedCompanyId(partnerId!);
      setIsSearching(false);
    }
  }, [isEditMode, existingCompany, partnerId]);

  // Populate arrangements when they load
  useEffect(() => {
    if (existingArrangements?.arrangements) {
      setArrangements(
        existingArrangements.arrangements.map((a) => ({
          arrangement_id: a.arrangement_id,
          destination_port_id: a.destination_port_id || '',
          destination_port_name: a.destination_port_name || '',
          destination_country_code: a.destination_country_code || '',
          notify_parties: a.notify_parties || [],
          document_recipients: a.document_recipients || [],
          stakeholders: a.stakeholders || [],
          special_instructions: a.special_instructions || '',
        }))
      );
    }
  }, [existingArrangements]);

  // Mutations
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const createAssociation = useCreateReceiverAssociation();
  const createArrangement = useCreateArrangement();
  const updateArrangement = useUpdateArrangement();
  const deleteArrangement = useDeleteArrangement();
  const uploadDocument = useUploadDocument();

  // Select existing company from search
  const handleSelectCompany = (company: CompanyResponse) => {
    const address = company.headquarters_address as Record<string, string> | null;
    setFormState({
      company_name: company.company_name || '',
      company_type: company.company_type || 'receiver',
      legal_name: company.legal_name || '',
      tax_id: company.tax_id || '',
      eori_number: company.eori_number || '',
      primary_email: company.primary_email || '',
      primary_phone: company.primary_phone || '',
      street: address?.street || '',
      city: address?.city || '',
      state_province: address?.state_province || '',
      postal_code: address?.postal_code || '',
      country_code: address?.country_code || '',
    });
    setSelectedCompanyId(company.company_id);
    setSearchTerm('');
    setShowSearchDropdown(false);
    setIsSearching(false);
  };

  // Create new company from search
  const handleCreateNew = () => {
    setFormState({
      ...initialFormState,
      company_name: searchTerm.trim(),
      company_type: presetType || 'receiver',
    });
    setSelectedCompanyId(null);
    setSearchTerm('');
    setShowSearchDropdown(false);
    setIsSearching(false);
  };

  // Back to search
  const handleBackToSearch = () => {
    setIsSearching(true);
    setSelectedCompanyId(null);
    setFormState({ ...initialFormState, company_type: presetType || 'receiver' });
    setArrangements([]);
  };

  // Country filtered options for autocomplete
  const filteredCountries = useMemo(() => {
    if (!countrySearch || countrySearch.length < 1) return [];
    const term = countrySearch.toLowerCase();
    return countries
      .filter((c) => c.label.toLowerCase().includes(term) || c.code.toLowerCase().includes(term))
      .slice(0, 10);
  }, [countrySearch]);

  // Get display label for selected country
  const selectedCountryLabel = useMemo(() => {
    if (!formState.country_code) return '';
    return countries.find((c) => c.id === formState.country_code)?.label || formState.country_code;
  }, [formState.country_code]);

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;
    if (!formState.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setIsSaving(true);

    try {
      let targetCompanyId = selectedCompanyId;

      // Build company payload
      const companyPayload = {
        company_name: formState.company_name.trim(),
        company_type: formState.company_type as CompanyType,
        legal_name: formState.legal_name.trim() || null,
        tax_id: formState.tax_id.trim() || null,
        eori_number: formState.eori_number?.trim() || null,
        primary_email: formState.primary_email.trim() || null,
        primary_phone: formState.primary_phone.trim() || null,
        address_line1: formState.street.trim() || null,
        city: formState.city.trim() || null,
        state_province: formState.state_province.trim() || null,
        postal_code: formState.postal_code.trim() || null,
        country_code: formState.country_code || null,
      };

      if (isEditMode && partnerId) {
        // Edit mode: update existing company
        await updateCompany.mutateAsync({
          companyId: partnerId,
          data: companyPayload,
        });
        targetCompanyId = partnerId;
      } else if (selectedCompanyId) {
        // Create mode with existing company selected: update it + create association
        await updateCompany.mutateAsync({
          companyId: selectedCompanyId,
          data: companyPayload,
        });
        // Create association (may already exist, API handles duplicates)
        try {
          await createAssociation.mutateAsync({
            shipper_company_id: companyId,
            receiver_company_id: selectedCompanyId,
          });
        } catch {
          // Association may already exist, that's fine
        }
        targetCompanyId = selectedCompanyId;
      } else {
        // Create mode with brand new company
        const newCompany = await createCompany.mutateAsync({
          ...companyPayload,
          is_active: true,
        });
        targetCompanyId = newCompany.company_id;

        // Create association
        await createAssociation.mutateAsync({
          shipper_company_id: companyId,
          receiver_company_id: newCompany.company_id,
        });
      }

      // Save document arrangements (only for receivers)
      if (formState.company_type === 'receiver' && targetCompanyId) {
        // Track existing arrangement IDs to detect deletions
        const existingIds = new Set(
          existingArrangements?.arrangements?.map((a) => a.arrangement_id) || []
        );
        const currentIds = new Set(
          arrangements.filter((a) => a.arrangement_id).map((a) => a.arrangement_id!)
        );

        // Delete removed arrangements
        for (const id of existingIds) {
          if (!currentIds.has(id)) {
            await deleteArrangement.mutateAsync(id);
          }
        }

        // Create or update arrangements
        for (const arr of arrangements) {
          const payload = {
            receiver_name: formState.company_name.trim(),
            receiver_company_id: targetCompanyId,
            destination_port_name: arr.destination_port_name || undefined,
            destination_port_id: arr.destination_port_id || undefined,
            destination_country_code: arr.destination_country_code || undefined,
            notify_parties: arr.notify_parties,
            document_recipients: arr.document_recipients,
            stakeholders: arr.stakeholders,
            special_instructions: arr.special_instructions || undefined,
          };

          if (arr.arrangement_id) {
            await updateArrangement.mutateAsync({
              arrangementId: arr.arrangement_id,
              data: payload,
            });
          } else {
            await createArrangement.mutateAsync(payload);
          }
        }

        // Upload attached PDFs (triggers async extraction)
        for (const arr of arrangements) {
          if (arr.file) {
            uploadDocument.mutate({
              file: arr.file,
              documentType: 'document_arrangement',
              receiverCompanyId: targetCompanyId,
            });
          }
        }
      }

      // Create key contacts (only new ones, on first save)
      if (keyContacts.length > 0 && targetCompanyId) {
        const role = companyTypeToRole[formState.company_type] || 'Receiver';
        for (const contact of keyContacts) {
          if (!contact.firstName && !contact.email) continue;
          try {
            await createContact({
              accountId: companyId,
              firstName: contact.firstName,
              lastName: contact.lastName,
              email: contact.email,
              phone: contact.phone || undefined,
              notes: contact.notes || undefined,
              companyName: formState.company_name,
              contactCompanyId: targetCompanyId,
              role: role as any,
            });
          } catch (err) {
            console.error('Failed to create contact:', err);
          }
        }
      }

      // Invalidate list caches so data is fresh on navigation
      await queryClient.invalidateQueries({ queryKey: ['business-partners'] });
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });

      toast.success(
        isEditMode ? 'Business partner updated successfully' : 'Business partner added successfully'
      );

      // Navigate back
      if (returnTo === 'container-form' && targetCompanyId) {
        navigate('/shipper/containers/new', {
          state: { newReceiverId: targetCompanyId },
        });
      } else {
        navigate('/shipper/business-partners', { state: navigationStateFrom(location) });
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to save business partner');
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditMode && isLoadingCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-lg text-tertiary">Loading...</p>
      </div>
    );
  }

  const showArrangements = formState.company_type === 'receiver';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <BackLink fallbackTo="/shipper/business-partners" />
        <div>
          <h1 className="text-2xl font-semibold text-primary">
            {isEditMode ? 'Edit Business Partner' : 'Add Business Partner'}
          </h1>
          <p className="text-tertiary mt-1">
            {isEditMode
              ? 'Update company information and document arrangements'
              : 'Search for an existing company or create a new one'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Search Card — create mode only, while still searching */}
        {!isEditMode && isSearching && (
          <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-4">Find Company</h2>
            <div className="relative">
              <Input
                placeholder="Search by company name, email, or tax ID..."
                value={searchTerm}
                onChange={(value) => {
                  setSearchTerm(value);
                  setShowSearchDropdown(true);
                }}
                onFocus={() => setShowSearchDropdown(true)}
                onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                icon={SearchLg}
                size="md"
              />
              {showSearchDropdown && searchTerm.length >= 2 && (
                <div className="absolute z-10 w-full mt-1 max-h-60 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                  {filteredSearchResults.map((company) => (
                    <button
                      key={company.company_id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectCompany(company)}
                    >
                      <span className="flex-1">
                        <span className="text-primary font-medium">{company.company_name}</span>
                        {company.primary_email && (
                          <span className="text-tertiary ml-2">{company.primary_email}</span>
                        )}
                      </span>
                      <Badge
                        color={getCompanyTypeBadgeColor(company.company_type)}
                        size="sm"
                      >
                        {formatCompanyType(company.company_type)}
                      </Badge>
                    </button>
                  ))}
                  {/* Create new option */}
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 border-t border-secondary"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleCreateNew}
                  >
                    <Plus className="size-4 text-brand-600" />
                    <span className="text-brand-600 font-medium">
                      Create new company: &quot;{searchTerm.trim()}&quot;
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form fields — shown when a company is selected or in edit mode */}
        {(!isSearching || isEditMode) && (
          <>
            {/* Back to search link (create mode only) */}
            {!isEditMode && (
              <div>
                <button
                  type="button"
                  onClick={handleBackToSearch}
                  className="text-sm text-brand-600 hover:text-brand-700 underline"
                >
                  &larr; Search for a different company
                </button>
              </div>
            )}

            {/* Company Info Card */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
              <h2 className="text-lg font-semibold text-primary mb-6">Company Information</h2>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                  label="Company Name"
                  placeholder="Enter company name"
                  value={formState.company_name}
                  onChange={(v) => setFormState((s) => ({ ...s, company_name: v }))}
                  size="md"
                  isRequired
                />
                <Select
                  label="Company Type"
                  placeholder="Select type"
                  items={companyTypeOptions}
                  selectedKey={formState.company_type}
                  onSelectionChange={(key) =>
                    setFormState((s) => ({ ...s, company_type: key as string }))
                  }
                  size="md"
                >
                  {(item) => (
                    <Select.Item key={item.id} id={item.id}>
                      {item.label}
                    </Select.Item>
                  )}
                </Select>
                <Input
                  label="Legal Name"
                  placeholder="Legal entity name"
                  value={formState.legal_name}
                  onChange={(v) => setFormState((s) => ({ ...s, legal_name: v }))}
                  size="md"
                />
                <Input
                  label="Tax ID (VAT)"
                  placeholder="Tax identification number"
                  value={formState.tax_id}
                  onChange={(v) => setFormState((s) => ({ ...s, tax_id: v }))}
                  size="md"
                />
                <Input
                  label="EORI Number"
                  placeholder="e.g. IT12345678901"
                  value={formState.eori_number}
                  onChange={(v) => setFormState((s) => ({ ...s, eori_number: v }))}
                  size="md"
                />
                <Input
                  label="Email"
                  placeholder="company@example.com"
                  value={formState.primary_email}
                  onChange={(v) => setFormState((s) => ({ ...s, primary_email: v }))}
                  size="md"
                />
                <Input
                  label="Phone"
                  placeholder="+1 234 567 890"
                  value={formState.primary_phone}
                  onChange={(v) => setFormState((s) => ({ ...s, primary_phone: v }))}
                  size="md"
                />
              </div>
            </div>

            {/* Address Card */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
              <h2 className="text-lg font-semibold text-primary mb-6">Address</h2>
              <div className="space-y-6">
                <Input
                  label="Street"
                  placeholder="Street address"
                  value={formState.street}
                  onChange={(v) => setFormState((s) => ({ ...s, street: v }))}
                  size="md"
                />
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Input
                    label="City"
                    placeholder="City"
                    value={formState.city}
                    onChange={(v) => setFormState((s) => ({ ...s, city: v }))}
                    size="md"
                  />
                  <Input
                    label="State / Province"
                    placeholder="State or province"
                    value={formState.state_province}
                    onChange={(v) => setFormState((s) => ({ ...s, state_province: v }))}
                    size="md"
                  />
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Input
                    label="Postal Code"
                    placeholder="Postal / ZIP code"
                    value={formState.postal_code}
                    onChange={(v) => setFormState((s) => ({ ...s, postal_code: v }))}
                    size="md"
                  />
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Country
                    </label>
                    {formState.country_code ? (
                      <div className="flex items-center gap-2">
                        <span className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
                          {selectedCountryLabel}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setFormState((s) => ({ ...s, country_code: '' }));
                            setCountrySearch('');
                          }}
                          className="text-tertiary hover:text-primary"
                        >
                          <XCircle className="size-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(value) => {
                            setCountrySearch(value);
                            setShowCountryDropdown(true);
                          }}
                          onFocus={() => setShowCountryDropdown(true)}
                          onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                          size="md"
                        />
                        {showCountryDropdown && filteredCountries.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                            {filteredCountries.map((country) => (
                              <button
                                key={country.id}
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-secondary"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setFormState((s) => ({ ...s, country_code: country.id }));
                                  setCountrySearch('');
                                  setShowCountryDropdown(false);
                                }}
                              >
                                {country.label} ({country.code})
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Key Contacts */}
            {!isEditMode && (
              <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-primary">Key Contacts</h2>
                    <p className="text-sm text-tertiary mt-1">
                      Add key people for this company. They will appear in your address book.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() =>
                      setKeyContacts([...keyContacts, { firstName: '', lastName: '', email: '', phone: '', notes: '' }])
                    }
                    color="secondary"
                    size="sm"
                    iconLeading={Plus}
                  >
                    Add Contact
                  </Button>
                </div>

                {keyContacts.length > 0 && (
                  <div className="space-y-4">
                    {keyContacts.map((contact, idx) => (
                      <div key={idx} className="grid grid-cols-1 gap-3 sm:grid-cols-6 items-end border-b border-secondary pb-4 last:border-0 last:pb-0">
                        <Input
                          label={idx === 0 ? 'First Name' : undefined}
                          placeholder="First name"
                          value={contact.firstName}
                          onChange={(v) => {
                            const updated = [...keyContacts];
                            updated[idx] = { ...updated[idx], firstName: v };
                            setKeyContacts(updated);
                          }}
                          size="md"
                        />
                        <Input
                          label={idx === 0 ? 'Last Name' : undefined}
                          placeholder="Last name"
                          value={contact.lastName}
                          onChange={(v) => {
                            const updated = [...keyContacts];
                            updated[idx] = { ...updated[idx], lastName: v };
                            setKeyContacts(updated);
                          }}
                          size="md"
                        />
                        <Input
                          label={idx === 0 ? 'Email' : undefined}
                          placeholder="email@company.com"
                          value={contact.email}
                          onChange={(v) => {
                            const updated = [...keyContacts];
                            updated[idx] = { ...updated[idx], email: v };
                            setKeyContacts(updated);
                          }}
                          size="md"
                        />
                        <Input
                          label={idx === 0 ? 'Phone' : undefined}
                          placeholder="+1 234 567"
                          value={contact.phone}
                          onChange={(v) => {
                            const updated = [...keyContacts];
                            updated[idx] = { ...updated[idx], phone: v };
                            setKeyContacts(updated);
                          }}
                          size="md"
                        />
                        <Input
                          label={idx === 0 ? 'Notes' : undefined}
                          placeholder="Role, notes..."
                          value={contact.notes}
                          onChange={(v) => {
                            const updated = [...keyContacts];
                            updated[idx] = { ...updated[idx], notes: v };
                            setKeyContacts(updated);
                          }}
                          size="md"
                        />
                        <button
                          type="button"
                          onClick={() => setKeyContacts(keyContacts.filter((_, i) => i !== idx))}
                          className="text-error-600 hover:text-error-700 p-2 self-end mb-1"
                        >
                          <Trash01 className="size-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Document Arrangements — receivers only */}
            {showArrangements && (
              <DocumentArrangementsSection
                receiverName={formState.company_name}
                receiverCompanyId={selectedCompanyId || partnerId}
                arrangements={arrangements}
                onArrangementsChange={setArrangements}
              />
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                onClick={() => navigate('/shipper/business-partners', { state: navigationStateFrom(location) })}
                color="secondary"
                size="lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                size="lg"
                iconLeading={Save01}
                isLoading={isSaving}
              >
                {isEditMode ? 'Update Partner' : 'Save Partner'}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};
